import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Plus, Search, CreditCard, Calendar, Edit, Trash2, CheckCircle, Upload, AlertTriangle } from 'lucide-react';
import { DataTable } from '../components/ui/data-table';
import { LoadingCard } from '../components/ui/loading-spinner';
import { paymentsApi, clientsApi, invoicesApi, documentsApi, billingApi } from '../lib/api';
import { formatDate, formatCurrency, getStatusBadgeClass, getStatusLabel, getErrorMessage } from '../lib/utils';
import { toast } from 'sonner';
import PaymentDialog from '../components/PaymentDialog';
import UploadResultsDialog from '../components/UploadResultsDialog';

export default function Payments() {
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [bulkResults, setBulkResults] = useState(null);

  // Тарифный флаг берём из /billing/subscription — единственный источник
  // правды (не дублируем список тарифов хардкодом на фронтенде).
  const { data: subscriptionData } = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: () => billingApi.getSubscription().then((res) => res.data),
  });
  const hasDocRecognition = !!subscriptionData?.subscription?.has_document_recognition;

  // Проверяем URL параметры при загрузке
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('action') === 'new') {
      handleCreateNew();
    }
  }, [location.search]);

  const { data: paymentsData, isLoading } = useQuery({
    queryKey: ['payments', { search, status: statusFilter, client_id: clientFilter }],
    queryFn: () => paymentsApi.getAll({ 
      search: search || undefined,
      status: statusFilter || undefined,
      client_id: clientFilter || undefined,
      skip: 0,
      limit: 50 
    }).then(res => res.data),
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-filter'],
    queryFn: () => clientsApi.getAll({ limit: 100 }).then(res => res.data),
  });

  const { data: invoicesData } = useQuery({
    queryKey: ['invoices-for-payments'],
    queryFn: () => invoicesApi.getAll({ limit: 100 }).then(res => res.data),
  });

  const deleteMutation = useMutation({
    mutationFn: paymentsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast.success('Платёж успешно удалён');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Ошибка при удалении платежа'));
    },
  });

  const markReceivedMutation = useMutation({
    mutationFn: (paymentId) => paymentsApi.markReceived(paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices-for-payments'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast.success('Платёж отмечен как полученный. Счёт обновлён.');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Ошибка при обновлении статуса платежа'));
    },
  });

  const handleEdit = (payment) => {
    setSelectedPayment(payment);
    setIsDialogOpen(true);
  };

  const handleDelete = (payment) => {
    if (window.confirm(`Вы уверены, что хотите удалить платёж на сумму ${formatCurrency(payment.amount)}?`)) {
      deleteMutation.mutate(payment.id);
    }
  };

  const handleMarkReceived = (payment) => {
    if (window.confirm(`Отметить платёж на сумму ${formatCurrency(payment.amount)} как полученный?`)) {
      markReceivedMutation.mutate(payment.id);
    }
  };

  const handleCreateNew = () => {
    setSelectedPayment(null);
    setIsDialogOpen(true);
  };

  const uploadPaymentMutation = useMutation({
    mutationFn: (files) => documentsApi.uploadPaymentsBulk(files),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices-for-payments'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      const { results, summary } = res.data;
      setBulkResults({
        summary,
        results: results.map((r) => ({
          filename: r.filename,
          status: r.status,
          primary: r.status === 'error' ? null : `Платёж ${formatCurrency(r.amount)}${r.matched_invoice_id ? ' — связан со счётом' : ''}`,
          detail: r.issues?.length ? r.issues.join('; ') : null,
        })),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Не удалось распознать файлы'));
    },
  });

  const handleUploadPayment = (e) => {
    // Копируем в массив немедленно: e.target.files — живая ссылка на FileList,
    // и сброс value ниже очищает её раньше, чем асинхронная мутация успевает
    // прочитать файлы.
    const files = Array.from(e.target.files || []);
    if (files.length) uploadPaymentMutation.mutate(files);
    e.target.value = '';
  };

  const getPaymentTypeLabel = (type) => {
    const types = {
      bank_transfer: 'Банковский перевод',
      cash: 'Наличные',
      card: 'Банковская карта',
      online: 'Онлайн платёж',
      other: 'Другое',
    };
    return types[type] || type;
  };

  const columns = [
    {
      header: 'Сумма',
      accessor: 'amount',
      cell: (payment) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 flex items-center gap-1.5" data-testid={`payment-amount-${payment.id}`}>
              {formatCurrency(payment.amount)}
              {payment.needs_review && (
                <span
                  title={payment.review_reason || 'Требует проверки — не найден связанный счёт'}
                  data-testid={`needs-review-payment-${payment.id}`}
                >
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                </span>
              )}
            </p>
            {payment.description && (
              <p className="text-xs text-gray-500 truncate max-w-[180px]">{payment.description}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      header: 'Клиент',
      accessor: 'client_id',
      cell: (payment) => {
        const client = clientsData?.clients?.find(c => c.id === payment.client_id);
        return (
          <div>
            <p className="text-sm font-medium text-gray-900">{client?.name || '—'}</p>
          </div>
        );
      },
    },
    {
      header: 'Счёт',
      accessor: 'invoice_id',
      cell: (payment) => {
        const invoice = invoicesData?.invoices?.find(inv => inv.id === payment.invoice_id);
        if (!invoice) return <span className="text-sm text-gray-400">Не привязан</span>;
        return (
          <div>
            <p className="text-sm font-medium text-gray-900">{invoice.number}</p>
            <p className="text-xs text-gray-500">{formatCurrency(invoice.amount || invoice.total || 0)}</p>
          </div>
        );
      },
    },
    {
      header: 'Статус',
      accessor: 'status',
      cell: (payment) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(payment.status)}`}>
          {getStatusLabel('payment', payment.status)}
        </span>
      ),
    },
    {
      header: 'Способ оплаты',
      accessor: 'payment_method',
      cell: (payment) => (
        <span className="text-sm text-gray-600">
          {getPaymentTypeLabel(payment.payment_method)}
        </span>
      ),
    },
    {
      header: 'Даты',
      accessor: 'dates',
      cell: (payment) => (
        <div>
          {payment.date_expected && (
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <Calendar className="w-4 h-4 mr-1 flex-shrink-0" />
              <span>Ожидание: {formatDate(payment.date_expected)}</span>
            </div>
          )}
          {payment.date_received && (
            <div className="flex items-center text-sm text-green-600">
              <Calendar className="w-4 h-4 mr-1 flex-shrink-0" />
              <span>Получен: {formatDate(payment.date_received)}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      header: 'Действия',
      accessor: 'actions',
      cell: (payment) => (
        <div className="flex items-center space-x-2">
          {payment.status === 'pending' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleMarkReceived(payment);
              }}
              className="text-green-600 hover:text-green-900 p-1 rounded"
              disabled={markReceivedMutation.isPending}
              data-testid={`mark-received-payment-${payment.id}`}
              title="Отметить как полученный"
            >
              <CheckCircle className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(payment);
            }}
            className="text-blue-600 hover:text-blue-900 p-1 rounded"
            data-testid={`edit-payment-${payment.id}`}
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(payment);
            }}
            className="text-red-600 hover:text-red-900 p-1 rounded"
            disabled={deleteMutation.isPending}
            data-testid={`delete-payment-${payment.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: '', label: 'Все статусы' },
    { value: 'pending', label: 'Ожидает оплаты' },
    { value: 'received', label: 'Оплачен' },
    { value: 'cancelled', label: 'Отменён' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Платежи</h1>
        </div>
        <LoadingCard />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Заголовок и действия */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Платежи</h1>
          <p className="text-gray-600 mt-1">
            Управление платежами ({paymentsData?.total || 0})
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept="application/pdf"
            multiple
            ref={fileInputRef}
            onChange={handleUploadPayment}
            className="hidden"
            data-testid="upload-payment-input"
          />
          {hasDocRecognition ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadPaymentMutation.isPending}
              className="border border-blue-600 text-blue-600 px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-50 transition-colors disabled:opacity-50"
              data-testid="upload-payment-btn"
            >
              <Upload className="w-5 h-5" />
              <span>{uploadPaymentMutation.isPending ? 'Распознаём...' : 'Загрузить PDF'}</span>
            </button>
          ) : (
            <a
              href="/billing"
              className="border border-gray-300 text-gray-400 px-4 py-2 rounded-lg flex items-center space-x-2"
              title="Загрузка платёжек с автораспознаванием доступна на тарифе Professional и выше"
              data-testid="upload-payment-upsell"
            >
              <Upload className="w-5 h-5" />
              <span>Загрузить PDF (Professional+)</span>
            </a>
          )}
          <button
            onClick={handleCreateNew}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors"
            data-testid="create-payment-btn"
          >
            <Plus className="w-5 h-5" />
            <span>Добавить платёж</span>
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Поиск */}
          <div className="flex items-center space-x-2">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Поиск по описанию или номеру..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 border-none outline-none text-gray-900 placeholder-gray-500"
              data-testid="search-payments-input"
            />
          </div>

          {/* Фильтр по статусу */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="status-filter-select"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Фильтр по клиенту */}
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="client-filter-select"
          >
            <option value="">Все клиенты</option>
            {clientsData?.clients?.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Таблица платежей */}
      <div className="bg-white rounded-lg border border-gray-200">
        <DataTable
          columns={columns}
          data={paymentsData?.payments || []}
          onRowClick={(payment) => {
            handleEdit(payment);
          }}
        />
      </div>

      {/* Диалог создания/редактирования */}
      <PaymentDialog
        payment={selectedPayment}
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedPayment(null);
        }}
      />

      {/* Итоги массовой загрузки */}
      <UploadResultsDialog
        isOpen={!!bulkResults}
        onClose={() => setBulkResults(null)}
        title="Результаты загрузки платёжек"
        summary={bulkResults?.summary}
        results={bulkResults?.results}
      />
    </div>
  );
}